import { useState } from 'react';
import { Mail, MapPin, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { submitContact } from '../lib/api';
import { toast } from 'sonner';

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await submitContact(formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    { icon: Mail, label: 'Email', value: 'support@ipvaluemanagements.com', desc: 'We reply within 24 hours' },
    { icon: MapPin, label: 'Address', value: '950 28th St SE', desc: '49508, MI' },
    { icon: Clock, label: 'Business Hours', value: 'Open 7 days/week', desc: '9am - 9pm Eastern' },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="contact-page">
      {/* Hero */}
      <section className="bg-[#003366] py-16">
        <div className="max-w-[1440px] mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Contact Us</h1>
          <p className="text-slate-300 max-w-xl mx-auto">
            Have a question, suggestion, or need assistance? We're here to help. 
            Reach out and our team will get back to you promptly.
          </p>
        </div>
      </section>

      <div className="max-w-[1440px] mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact info */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border p-6 space-y-6">
              <h2 className="text-xl font-bold text-slate-900">Get in Touch</h2>
              
              {contactInfo.map((item, i) => (
                <div key={i} className="flex gap-4">
                  <div className="w-12 h-12 bg-[#003366]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="text-[#003366]" size={24} />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{item.label}</p>
                    <p className="text-[#003366]">{item.value}</p>
                    <p className="text-sm text-slate-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Map placeholder */}
            <div className="mt-6 bg-slate-200 rounded-lg h-48 flex items-center justify-center">
              <div className="text-center text-slate-500">
                <MapPin size={32} className="mx-auto mb-2" />
                <p>Interactive Map</p>
              </div>
            </div>
          </div>

          {/* Contact form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg border p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-6">Send us a Message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Your Name *</Label>
                    <Input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      className="mt-1"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className="mt-1"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="How can we help?"
                    className="mt-1"
                    data-testid="contact-subject-input"
                  />
                </div>

                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us more about your inquiry..."
                    rows={6}
                    className="mt-1"
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full md:w-auto bg-[#003366] hover:bg-[#002855] py-6 px-8"
                  data-testid="contact-submit-btn"
                >
                  <Send size={18} className="mr-2" />
                  {loading ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>

            {/* FAQ prompt */}
            <div className="mt-6 bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-lg p-6">
              <h3 className="font-semibold text-slate-900 mb-2">Looking for quick answers?</h3>
              <p className="text-slate-600 text-sm">
                Check out our <a href="/faq" className="text-[#003366] font-semibold hover:underline">FAQ section</a> for 
                answers to commonly asked questions about orders, shipping, returns, and more.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
